import { Component, inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { LocalhostKeys } from '@core/constants';
import { initializeStylesApp } from '@core/helpers';
import { environment } from '@root/environments/environment';
import { filter, Subject, takeUntil } from 'rxjs';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import {
  EventMessage,
  EventType,
  InteractionStatus,
  AuthenticationResult,
} from '@azure/msal-browser';
import { AuthStore } from './store/auth.store';
import { ApplicationInsightsService } from '@core/services/application-insights.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent implements OnDestroy {
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();
  authStore = inject(AuthStore);

  constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private meta: Meta,
    private title: Title,
    private readonly appInsights: ApplicationInsightsService,
  ) {
    initializeStylesApp();
    this.getGraphMeta();
    this.title.setTitle(environment.branding.text.openGraph.title);
    localStorage.setItem(LocalhostKeys.GOOGLE_MAP_KEY, environment.api.thirdParty.googleMaps.key);
  }

  ngOnInit(): void {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        this.authService.instance.handleRedirectPromise().then(result => {
          if (result) {
            this.authService.instance.setActiveAccount(result.account);
          }
        });
        // MSAL has settled — the app's auth state is now resolved either way.
        this.authStore.markReady();
        this.setLoginDisplay();
      });

    // Initial interactive login -> set token + navigate (handled by the store).
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$),
      )
      .subscribe(msg => this.authStore.loggedInViaMSAL(msg));

    // Silent token acquisition -> update the token only, no re-navigation.
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
        takeUntil(this._destroying$),
      )
      .subscribe(msg => this.authStore.updateMsalToken(msg.payload as AuthenticationResult));
  }

  // If the user is logged in, present the user with a "logged in" experience
  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  getGraphMeta() {
    this.meta.updateTag({
      property: 'og:url',
      content: environment.branding.text.openGraph.url,
    });
    this.meta.updateTag({
      property: 'twitter:url',
      content: environment.branding.text.openGraph.url,
    });
    this.meta.updateTag({
      property: 'og:title',
      content: environment.branding.text.openGraph.title,
    });
    this.meta.updateTag({
      property: 'twitter:title',
      content: environment.branding.text.openGraph.title,
    });
    this.meta.updateTag({
      property: 'og:description',
      content: environment.branding.text.openGraph.description,
    });
    this.meta.updateTag({
      property: 'twitter:description',
      content: environment.branding.text.openGraph.description,
    });
    this.meta.updateTag({
      property: 'twitter:domain',
      content: environment.branding.text.openGraph.url.replace('https://', ''),
    });
  }
}
